import { type FC, type ReactNode } from 'react';

import { Box } from '../../Box';
import { Divider } from '../../Divider';
import { Icon } from '../../Icon';
import { Text } from '../../Typography';
import {
  accordionIconOpenStyles,
  accordionIconStyles,
  accordionItemStyles,
  accordionPanelStyles,
  accordionTriggerStyles,
} from '../Accordion.styles';

export type AccordionItemProps = {
  /**
   * Unique identifier for this item
   */
  id: string;
  /**
   * Question or trigger label
   */
  title: ReactNode;
  /**
   * Answer or panel content
   */
  content: ReactNode;
  /**
   * Whether this panel is currently expanded
   */
  isOpen: boolean;
  /**
   * Toggle handler for this item
   */
  onToggle: () => void;
  /**
   * Whether to show a divider below this item
   */
  showDivider?: boolean;
};

const AccordionItem: FC<AccordionItemProps> = ({
  id,
  title,
  content,
  isOpen,
  onToggle,
  showDivider = true,
}) => {
  const triggerId = `accordion-trigger-${id}`;
  const panelId = `accordion-panel-${id}`;

  return (
    <Box css={accordionItemStyles}>
      <button
        type="button"
        id={triggerId}
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={onToggle}
        css={accordionTriggerStyles}
      >
        <Text as="span" size="md" fontWeight="bold">
          {title}
        </Text>
        <Icon
          variant="AngleDownSharp"
          size="large"
          css={[accordionIconStyles, isOpen && accordionIconOpenStyles]}
          aria-hidden
        />
      </button>

      <Box
        id={panelId}
        role="region"
        aria-labelledby={triggerId}
        hidden={!isOpen}
        css={accordionPanelStyles}
      >
        <Text as="div" size="md">
          {content}
        </Text>
      </Box>

      {showDivider && <Divider />}
    </Box>
  );
};

export default AccordionItem;
